import { useCallback, useEffect, useRef, useState } from 'react'

import {
  CHIME_STORAGE_KEY,
  chimeEnabled,
  chimeTones,
  storedChimeValue,
  type ChimeKind,
} from '../arrival-chime'

/**
 * Plays the arrival chime, and remembers whether this device wants it — §9.110.
 *
 * <p><b>One `AudioContext`, created on the first play and not before.</b> Constructing one on mount
 * would leave a suspended context on every board in the building; browsers also refuse to start one
 * without a user gesture, and the toggle is that gesture.
 *
 * <p><b>Failure is silent, deliberately.</b> If audio is blocked, unavailable, or the device has no
 * output, a dispatcher still has the toast and the board. A screen that threw because it could not
 * beep would be worse than one that did not beep.
 */
export function useArrivalChime(): {
  enabled: boolean
  toggle: () => void
  play: (kind: ChimeKind) => void
} {
  const [enabled, setEnabled] = useState(() => {
    try {
      return chimeEnabled(localStorage.getItem(CHIME_STORAGE_KEY))
    } catch {
      return false
    }
  })
  const contextRef = useRef<AudioContext | null>(null)

  useEffect(
    () => () => {
      void contextRef.current?.close()
      contextRef.current = null
    },
    [],
  )

  const play = useCallback(
    (kind: ChimeKind) => {
      if (!enabled) return
      try {
        const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        if (!Ctor) return
        contextRef.current ??= new Ctor()
        const context = contextRef.current
        void context.resume()
        for (const tone of chimeTones(kind)) {
          const oscillator = context.createOscillator()
          const gain = context.createGain()
          oscillator.connect(gain)
          gain.connect(context.destination)
          oscillator.type = 'square'
          oscillator.frequency.value = tone.hertz
          // Ramped rather than switched: an abrupt stop on a square wave clicks, and a click on every
          // beep is what makes a notification sound cheap.
          gain.gain.setValueAtTime(0.0001, context.currentTime + tone.at)
          gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + tone.at + 0.01)
          gain.gain.exponentialRampToValueAtTime(
            0.0001,
            context.currentTime + tone.at + tone.duration,
          )
          oscillator.start(context.currentTime + tone.at)
          oscillator.stop(context.currentTime + tone.at + tone.duration + 0.02)
        }
      } catch {
        // Blocked, unavailable, or no output. The toast still tells the story.
      }
    },
    [enabled],
  )

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current
      try {
        localStorage.setItem(CHIME_STORAGE_KEY, storedChimeValue(next))
      } catch {
        // A browser refusing storage still gets the sound for this session.
      }
      return next
    })
  }, [])

  return { enabled, toggle, play }
}
